import { API_URL } from '@/config';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Cake, Heart, Phone, Mail } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import axios from 'axios';
import { toast } from 'sonner';
import { getImageUrl } from '@/utils/imageUrl';

const API = `${API_URL}/api`;

const MembersListPage = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    birthday_month: '',
    anniversary_month: ''
  });

  useEffect(() => {
    loadMembers();
  }, [searchTerm, filters]);

  const loadMembers = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filters.category) params.append('category', filters.category);
      if (filters.birthday_month) params.append('birthday_month', filters.birthday_month);
      if (filters.anniversary_month) params.append('anniversary_month', filters.anniversary_month);
      
      const response = await axios.get(`${API}/members?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
      });
      setMembers(response.data);
    } catch (error) {
      console.error('Error loading members:', error);
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const isTodayBirthday = (dob) => {
    if (!dob) return false;
    const today = new Date();
    const birthDate = new Date(dob);
    return today.getMonth() === birthDate.getMonth() && today.getDate() === birthDate.getDate();
  };

  const isTodayAnniversary = (date) => {
    if (!date) return false;
    const today = new Date();
    const anniversary = new Date(date);
    return today.getMonth() === anniversary.getMonth() && today.getDate() === anniversary.getDate();
  };

  const clearFilters = () => {
    setFilters({ category: '', birthday_month: '', anniversary_month: '' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="members-list-page">
      <div>
        <h1 className="text-4xl font-bold mb-2" style={{ color: '#0F172A' }}>Members</h1>
        <p className="text-muted-foreground">Connect with our Lions family</p>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, mobile, profession..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline" data-testid="filter-btn">
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </SheetTrigger>
          <SheetContent data-testid="filter-sheet">
            <SheetHeader>
              <SheetTitle>Filter Members</SheetTitle>
            </SheetHeader>
            <div className="space-y-4 mt-6">
              <div>
                <label className="text-sm font-medium mb-2 block">Birthday Month</label>
                <Select value={filters.birthday_month} onValueChange={(v) => setFilters({...filters, birthday_month: v})}>
                  <SelectTrigger data-testid="birthday-month-select">
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">January</SelectItem>
                    <SelectItem value="2">February</SelectItem>
                    <SelectItem value="3">March</SelectItem>
                    <SelectItem value="4">April</SelectItem>
                    <SelectItem value="5">May</SelectItem>
                    <SelectItem value="6">June</SelectItem>
                    <SelectItem value="7">July</SelectItem>
                    <SelectItem value="8">August</SelectItem>
                    <SelectItem value="9">September</SelectItem>
                    <SelectItem value="10">October</SelectItem>
                    <SelectItem value="11">November</SelectItem>
                    <SelectItem value="12">December</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Anniversary Month</label>
                <Select value={filters.anniversary_month} onValueChange={(v) => setFilters({...filters, anniversary_month: v})}>
                  <SelectTrigger data-testid="anniversary-month-select">
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">January</SelectItem>
                    <SelectItem value="2">February</SelectItem>
                    <SelectItem value="3">March</SelectItem>
                    <SelectItem value="4">April</SelectItem>
                    <SelectItem value="5">May</SelectItem>
                    <SelectItem value="6">June</SelectItem>
                    <SelectItem value="7">July</SelectItem>
                    <SelectItem value="8">August</SelectItem>
                    <SelectItem value="9">September</SelectItem>
                    <SelectItem value="10">October</SelectItem>
                    <SelectItem value="11">November</SelectItem>
                    <SelectItem value="12">December</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button onClick={clearFilters} variant="outline" className="w-full" data-testid="clear-filters-btn">
                Clear Filters
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
      
      {members.length === 0 ? (
        <Card data-testid="no-members-card">
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">No members found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="members-grid">
          {members.map((member) => (
            <Link key={member.user_id} to={`/app/members/${member.user_id}`}>
              <Card className="hover-lift cursor-pointer" data-testid={`member-card-${member.user_id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <img
                      src={getImageUrl(member.photo_url) || 'https://api.dicebear.com/7.x/avataaars/svg?seed=Lion'}
                      alt={member.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold mb-1 truncate" style={{ color: '#0F172A' }}>
                        {member.name}
                      </h3>
                      <p className="text-sm text-muted-foreground truncate">
                        {member.professional_category || 'Member'}
                      </p>
                      <div className="flex gap-2 mt-2">
                        {isTodayBirthday(member.date_of_birth) && (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                            <Cake className="w-3 h-3" />
                            Birthday
                          </span>
                        )}
                        {isTodayAnniversary(member.wedding_anniversary_date) && (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#FCE7F3', color: '#9F1239' }}>
                            <Heart className="w-3 h-3" />
                            Anniversary
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm text-muted-foreground">
                    {member.mobile && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        <span className="truncate">{member.mobile}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{member.email}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default MembersListPage;
